<template>
  <div :class="$style.container">
    <q-select
      :model-value="selectedUserId"
      mode="single"
      class="mb-1 w-full"
      :field-label="$t('pins.modal.pin_under')"
      data-testid="pins-folder-picker"
      :placeholder="$t('pins.modal.search_user')"
      :options="usersList"
      :no-options-text="$t('universal.no_users_found')"
      :filter-results="false"
      @update:model-value="handleInput"
      @search-change="($event) => (query = $event)"
    >
      <template #option="{ option }">
        <div v-if="option.id === 'separator'" :class="$style.separator" />
      </template>

      <template #icon="{ open }">
        <search-icon v-if="open" class="icon-m" />
        <folder-icon v-else class="icon-m" />
      </template>

      <template #afterlist>
        <span v-if="remainingUsers" :class="$style.more">{{
          $t('universal.n_more', { n: $n(Number(remainingUsers)) }, Number(remainingUsers))
        }}</span>
      </template>
    </q-select>
    <p class="text-gray-70 text-sm font-normal">{{ $t('pins.modal.who_can_see_pins') }}</p>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, type PropType, ref, watch } from 'vue'
import { SearchIcon, FolderIcon } from '@klausapp/ui-kit/icons'

import QSelect from '@/components/QSelect.vue'
import type { AccountUser } from '@/modules/user-management/types'

const MAX_USERS_SHOWN = 10

export default defineComponent({
  name: 'FolderPicker',
  components: { SearchIcon, FolderIcon, QSelect },
  props: {
    selectedUserId: String as PropType<string>,
    accountUsers: {
      type: Array as PropType<AccountUser[]>,
      required: true,
    },
  },
  setup(props, context) {
    const pickedMember = ref<AccountUser | undefined>(undefined)
    const query = ref('')

    watch(
      () => props.accountUsers,
      (accountUsers) => {
        pickedMember.value = accountUsers.find(({ id }) => id === props.selectedUserId)
      },
    )

    onMounted(() => {
      pickedMember.value = props.accountUsers.find(({ id }) => id === props.selectedUserId)
    })

    const unselected = computed(() => visibleAccountUsers.value.filter((u) => u.id !== props.selectedUserId))

    const filteredUsers = computed(() =>
      props.accountUsers.filter(({ name }) => name.toLowerCase().includes(query.value.toLowerCase())),
    )

    const visibleAccountUsers = computed(() => filteredUsers.value.slice(0, MAX_USERS_SHOWN))

    const remainingUsers = computed(() =>
      Math.max(filteredUsers.value.length - (pickedMember.value ? 1 : 0) - unselected.value.length, 0),
    )

    return {
      pickedMember,
      visibleAccountUsers,
      // computed
      usersList: computed(() => {
        const list: { id: string | null; name: string | null }[] = []

        if (pickedMember.value) list.push(pickedMember.value)

        const needsSeparator = pickedMember.value && unselected.value.length
        if (needsSeparator) list.push({ id: 'separator', name: null })
        list.push(...unselected.value)

        return list
      }),
      handleInput(event: string) {
        pickedMember.value = props.accountUsers.find((m) => event === m.id)
        context.emit('input', event)
      },
      remainingUsers,
      query,
    }
  },
})
</script>

<style lang="scss" module>
.container {
  width: 100%;
  margin-bottom: 24px;
}

.separator {
  margin: 0 -16px;
  background-color: var(--gray-30);
  height: 1px;
  flex: 1;
}

.more {
  color: var(--gray-60);
}
</style>
